import React, { useState, useEffect, useContext, useRef } from "react";
import {
    Box,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    Chip,
    CircularProgress,
    Tooltip,
    Stack,
    Menu,
    MenuItem,
    ListItemIcon,
    useTheme,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CheckIcon from "@mui/icons-material/Check";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import Axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import { tokens } from "../../theme";
import { getAuthHeader, getAuthToken } from "../../utils/authToken";
import { SocketContext } from "../../socket";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);

const APTestProgress = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const socket = useContext(SocketContext);

    const [date, setDate] = useState(dayjs().tz("America/New_York"));
    const [thisWeek, setThisWeek] = useState([]);
    const thisWeekRef = useRef([]);
    const [semesterWeekNum, setSemesterWeekNum] = useState(null);
    const [semesterSeason, setSemesterSeason] = useState(null);
    const [loading, setLoading] = useState(true);
    const [coursesData, setCoursesData] = useState([]);
    const [editingCell, setEditingCell] = useState(null);
    const [editValue, setEditValue] = useState("");
    const [activeUsers, setActiveUsers] = useState({});
    const [socketIoUsers, setSocketIoUsers] = useState({});
    const [editingCells, setEditingCells] = useState({}); // { cellKey: { admin_uuid, admin_name } }

    const STORAGE_KEY = "apTestProgress_hiddenCols";
    const COL_WIDTH_KEY = "apTestProgress_colWidths";
    const allColumns = [
        { key: "course", label: "수업", minWidth: 50, defaultWidth: 140 },
        { key: "classroom", label: "교실", minWidth: 50, defaultWidth: 60 },
        { key: "teacher", label: "선생님", minWidth: 50, defaultWidth: 100 },
        { key: "testTitle", label: "Test Title", minWidth: 50, defaultWidth: 160 },
        { key: "student", label: "학생", minWidth: 50, defaultWidth: 120 },
        { key: "source", label: "출처", minWidth: 50, defaultWidth: 80 },
        { key: "reviewSchedule", label: "모의고사 리뷰 일정", minWidth: 50, defaultWidth: 100 },
        { key: "reviewProgress", label: "실제 리뷰 진도", minWidth: 50, defaultWidth: 140 },
        { key: "reviewProgressUnmatched", label: "미분류", minWidth: 50, defaultWidth: 140 },
        { key: "paperOutDate", label: "시험지 나가야 하는 날", minWidth: 50, defaultWidth: 100 },
        { key: "assignedDate", label: "나간날짜", minWidth: 50, defaultWidth: 80 },
        { key: "dueDate", label: "Due Date", minWidth: 50, defaultWidth: 70 },
        { key: "submitted", label: "제출", minWidth: 40, defaultWidth: 50 },
        { key: "submittedAt", label: "학생 제출", minWidth: 70, defaultWidth: 110 },
        { key: "adminSubmittedAt", label: "관리자 제출", minWidth: 70, defaultWidth: 110 },
        { key: "mcq", label: "MCQ", minWidth: 50, defaultWidth: 70 },
        { key: "frq", label: "FRQ", minWidth: 50, defaultWidth: 70 },
        { key: "apScore", label: "점수", minWidth: 40, defaultWidth: 50 },
        { key: "actualResult", label: "실제 시험 결과", minWidth: 120, defaultWidth: 200 },
    ];

    // Column width resizing
    const [colWidths, setColWidths] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(COL_WIDTH_KEY)) || {};
        } catch { return {}; }
    });
    const resizingRef = useRef(null);

    const getColWidth = (key) => colWidths[key] || allColumns.find(c => c.key === key)?.defaultWidth || 80;

    const handleResizeStart = (e, colKey) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = getColWidth(colKey);
        const minW = allColumns.find(c => c.key === colKey)?.minWidth || 40;

        const onMouseMove = (ev) => {
            const newWidth = Math.max(minW, startWidth + ev.clientX - startX);
            setColWidths(prev => {
                const next = { ...prev, [colKey]: newWidth };
                localStorage.setItem(COL_WIDTH_KEY, JSON.stringify(next));
                return next;
            });
        };

        const onMouseUp = () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
            resizingRef.current = null;
        };

        resizingRef.current = colKey;
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    const [hiddenCols, setHiddenCols] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
        } catch { return []; }
    });

    // Toggle for splitting the 실제 리뷰 진도 column to show the unmatched
    // sub-column. Default off (legacy/null entries hidden).
    const SHOW_UNMATCHED_KEY = "apTestProgress_showUnmatched";
    const [showUnmatched, setShowUnmatched] = useState(() => {
        try { return JSON.parse(localStorage.getItem(SHOW_UNMATCHED_KEY)) || false; }
        catch { return false; }
    });
    const toggleShowUnmatched = () => {
        const next = !showUnmatched;
        setShowUnmatched(next);
        localStorage.setItem(SHOW_UNMATCHED_KEY, JSON.stringify(next));
    };

    const isVisible = (key) => {
        if (key === "reviewProgressUnmatched") {
            // Only visible when toggle is on AND the matched column itself is visible.
            return showUnmatched && !hiddenCols.includes("reviewProgress");
        }
        return !hiddenCols.includes(key);
    };

    // Inline reassignment popover state — admins can click any review_progress
    // entry (matched or unmatched) to move it between tests/N/A on this board.
    const [reassignMenu, setReassignMenu] = useState(null);
    // shape: { anchorEl, eventData, slot, courseTests, currentTestRef } | null

    const handleEntryClick = async (e, entry, course) => {
        e.stopPropagation();
        const anchorEl = e.currentTarget;
        try {
            const res = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/events/${entry.event_id}/progress`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setReassignMenu({
                anchorEl,
                eventData: res.data,
                slot: entry.slot,
                courseTests: course.tests || [],
                // Use resolved_test_id so regex-matched entries highlight the
                // bucketed test (entry.test_ref alone is null for those).
                currentTestRef: entry.resolved_test_id ?? entry.test_ref ?? null,
            });
        } catch (err) {
            console.error("Failed to fetch event for reassignment:", err);
        }
    };

    const handleReassignSelect = async (newTestId) => {
        if (!reassignMenu) return;
        const { eventData, slot } = reassignMenu;
        const progressCount = eventData.progress_3 ? 3 : eventData.progress_2 ? 2 : 1;
        const body = {
            progress: eventData.progress,
            progress_from: eventData.progress_from,
            progress_to: eventData.progress_to,
            progress_2: eventData.progress_2,
            progress_from_2: eventData.progress_from_2,
            progress_to_2: eventData.progress_to_2,
            progress_3: eventData.progress_3,
            progress_from_3: eventData.progress_from_3,
            progress_to_3: eventData.progress_to_3,
            progress_count: progressCount,
            test_ref_1: eventData.test_ref_1,
            test_ref_2: eventData.test_ref_2,
            test_ref_3: eventData.test_ref_3,
        };
        body[`test_ref_${slot}`] = newTestId; // override the chosen slot
        setReassignMenu(null);
        try {
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/events/${eventData.id}/progress`,
                body,
                { headers: { Authorization: getAuthHeader() } }
            );
            // Server emits global.apTestFRQGraded → existing onFRQGraded listener refetches.
        } catch (err) {
            console.error("Reassignment failed:", err);
            alert(err.response?.data?.message || "Failed to reassign — see console");
        }
    };

    const hideColumn = (key) => {
        const next = [...hiddenCols, key];
        setHiddenCols(next);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    };

    const showColumn = (key) => {
        const next = hiddenCols.filter((k) => k !== key);
        setHiddenCols(next);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    };

    const visibleColCount = allColumns.filter((c) => isVisible(c.key)).length;

    const seasonLabels = { 1: "Spring", 2: "Summer", 3: "Fall", 4: "Winter" };

    // Socket connection + listeners
    useEffect(() => {
        function onConnect() {
            console.log("[APTestProgress] Socket connected");
            socket.emit("join.apTestProgress");
        }

        // Global users list (for names/colors)
        function onGlobalUsers(data) {
            setSocketIoUsers(data.io_users || {});
        }

        // Full active user list for this page (room-based)
        function onPageUsers(users) {
            setActiveUsers(users || {});
        }

        // Someone started/stopped editing a cell
        function onEditingCell(data) {
            const { cellKey, editing, admin_uuid, admin_name } = data;
            setEditingCells((prev) => {
                const next = { ...prev };
                if (editing) {
                    next[cellKey] = { admin_uuid, admin_name };
                } else {
                    delete next[cellKey];
                }
                return next;
            });
        }

        // Manual field updated by another user
        function onManualUpdated(data) {
            const { course_test_id, field, value } = data;
            setCoursesData((prev) =>
                prev.map((course) => ({
                    ...course,
                    tests: course.tests.map((t) =>
                        t.course_test_id === course_test_id ? { ...t, [field]: value } : t
                    ),
                }))
            );
        }

        // Actual result updated by another user
        function onResultUpdated(data) {
            const { student_id, course_id, score } = data;
            setCoursesData((prev) =>
                prev.map((course) => {
                    if (course.course_id !== course_id) return course;
                    return {
                        ...course,
                        tests: course.tests.map((t) => ({
                            ...t,
                            students: t.students.map((s) =>
                                s.student_id === student_id
                                    ? { ...s, actual_exam_result: score }
                                    : s
                            ),
                        })),
                    };
                })
            );
        }

        // Reminder count updated by another user
        function onReminderUpdated(data) {
            const { course_test_id, student_id, count } = data;
            setCoursesData((prev) =>
                prev.map((course) => ({
                    ...course,
                    tests: course.tests.map((t) =>
                        t.course_test_id === course_test_id
                            ? { ...t, reminder_counts: { ...t.reminder_counts, [student_id]: count } }
                            : t
                    ),
                }))
            );
        }

        // FRQ grading completed or score override changed — re-fetch to get updated scores
        function onFRQGraded() {
            const week = thisWeekRef.current;
            if (week.length > 0) {
                Axios.get(
                    `${process.env.REACT_APP_URL}/api/v1/ap-test-progress`,
                    {
                        params: { start_of_week: week[0], end_of_week: week[6] },
                        headers: { Authorization: getAuthHeader() },
                    }
                ).then((res) => {
                    setCoursesData(res.data.courses || []);
                }).catch((err) => {
                    console.error("Failed to refresh after FRQ grading:", err);
                });
            }
        }

        // Register listeners BEFORE connecting
        socket.on("connect", onConnect);
        socket.on("global.users", onGlobalUsers);
        socket.on("global.apTestProgressUsers", onPageUsers);
        socket.on("global.apTestEditingCell", onEditingCell);
        socket.on("global.apTestManualUpdated", onManualUpdated);
        socket.on("global.apTestResultUpdated", onResultUpdated);
        socket.on("global.apTestReminderUpdated", onReminderUpdated);
        socket.on("global.apTestFRQGraded", onFRQGraded);

        // Set auth and connect
        const token = getAuthToken();
        socket.auth = { token };
        socket.connect();

        // If already connected, connect() is a no-op and connect event won't fire
        if (socket.connected) {
            socket.emit("join.apTestProgress");
        }

        // Fallback: if connection is still establishing, wait and try again
        const fallbackTimer = setTimeout(() => {
            if (socket.connected) {
                socket.emit("join.apTestProgress");
            }
        }, 500);

        return () => {
            clearTimeout(fallbackTimer);
            socket.emit("leave.apTestProgress");
            socket.off("connect", onConnect);
            socket.off("global.users", onGlobalUsers);
            socket.off("global.apTestProgressUsers", onPageUsers);
            socket.off("global.apTestEditingCell", onEditingCell);
            socket.off("global.apTestManualUpdated", onManualUpdated);
            socket.off("global.apTestResultUpdated", onResultUpdated);
            socket.off("global.apTestReminderUpdated", onReminderUpdated);
            socket.off("global.apTestFRQGraded", onFRQGraded);
            socket.disconnect();
        };
    }, []);

    // On date change, recalculate week + fetch semester info
    useEffect(() => {
        let ignore = false;

        function findThisWeekWithDate(d) {
            const week = [];
            for (let i = 1; i < 8; i++) {
                week.push(d.startOf("week").add(i, "day").format("YYYY-MM-DD"));
            }
            setThisWeek(week);
            thisWeekRef.current = week;
        }

        async function findSemesterAndSemesterWeekNum(d) {
            const wednesday = d.startOf("week").add(3, "day");
            await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/weekly/semester-weeks?date=${wednesday.format("YYYY-MM-DD HH:mm:ss")}`,
                { headers: { Authorization: getAuthHeader() } }
            ).then((res) => {
                setSemesterSeason(res.data.semester_season);
                setSemesterWeekNum(res.data.semester_week_num);
            }).catch((err) => {
                console.error("Failed to fetch semester info:", err);
            });
        }

        if (!ignore) {
            findThisWeekWithDate(date);
            findSemesterAndSemesterWeekNum(date);
        }

        return () => { ignore = true; };
    }, [date]);

    // On week change, fetch main data
    useEffect(() => {
        let ignore = false;

        async function fetchData() {
            if (thisWeek.length === 0) return;
            setLoading(true);
            await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/ap-test-progress`,
                {
                    params: {
                        start_of_week: thisWeek[0],
                        end_of_week: thisWeek[6],
                    },
                    headers: { Authorization: getAuthHeader() },
                }
            ).then((res) => {
                if (!ignore) {
                    setCoursesData(res.data.courses || []);
                }
            }).catch((err) => {
                console.error("Failed to fetch AP test progress:", err);
            }).finally(() => {
                if (!ignore) setLoading(false);
            });
        }

        fetchData();

        return () => { ignore = true; };
    }, [thisWeek]);

    // Navigation
    const goToPreviousWeek = () => setDate((prev) => prev.subtract(1, "week"));
    const goToNextWeek = () => setDate((prev) => prev.add(1, "week"));

    // Save manual field for a course_test
    const saveManualField = async (courseTestId, field, value) => {
        try {
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/ap-test-progress/manual/${courseTestId}`,
                { [field]: value },
                { headers: { Authorization: getAuthHeader(), "Content-Type": "application/json" } }
            );
            // Update local state
            setCoursesData((prev) =>
                prev.map((course) => ({
                    ...course,
                    tests: course.tests.map((t) =>
                        t.course_test_id === courseTestId ? { ...t, [field]: value } : t
                    ),
                }))
            );
            // Broadcast to other users
            socket.emit("put.apTestManual", { course_test_id: courseTestId, field, value });
        } catch (err) {
            console.error("Failed to save manual field:", err);
        }
    };

    // Save actual exam result
    const handleReminderClick = async (courseTestId, studentId) => {
        try {
            const res = await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/ap-test-progress/reminder/${courseTestId}/${studentId}`,
                {},
                { headers: { Authorization: getAuthHeader() } }
            );
            const newCount = res.data.reminder_count;
            setCoursesData((prev) =>
                prev.map((course) => ({
                    ...course,
                    tests: course.tests.map((t) =>
                        t.course_test_id === courseTestId
                            ? { ...t, reminder_counts: { ...t.reminder_counts, [studentId]: newCount } }
                            : t
                    ),
                }))
            );
            socket.emit("put.apTestReminder", { course_test_id: courseTestId, student_id: studentId, count: newCount });
        } catch (err) {
            console.error("Failed to update reminder count:", err);
        }
    };

    const saveActualResult = async (studentId, courseId, score) => {
        try {
            await Axios.put(
                `${process.env.REACT_APP_URL}/api/v1/ap-test-progress/actual-result`,
                { student_id: studentId, course_id: courseId, score: score || null },
                { headers: { Authorization: getAuthHeader(), "Content-Type": "application/json" } }
            );
            // Update local state
            setCoursesData((prev) =>
                prev.map((course) => {
                    if (course.course_id !== courseId) return course;
                    return {
                        ...course,
                        tests: course.tests.map((t) => ({
                            ...t,
                            students: t.students.map((s) =>
                                s.student_id === studentId
                                    ? { ...s, actual_exam_result: score || null }
                                    : s
                            ),
                        })),
                    };
                })
            );
            // Broadcast to other users
            socket.emit("put.apTestResult", { student_id: studentId, course_id: courseId, score: score || null });
        } catch (err) {
            console.error("Failed to save actual result:", err);
        }
    };

    // Editable cell handlers
    const editInputRef = useRef(null);
    const startEditing = (key, currentValue) => {
        setEditingCell(key);
        setEditValue(currentValue || "");
        socket.emit("put.apTestEditingCell", { cellKey: key, editing: true });
    };

    const stopEditing = () => {
        if (editingCell) {
            socket.emit("put.apTestEditingCell", { cellKey: editingCell, editing: false });
        }
        setEditingCell(null);
    };

    const handleEditBlur = (key, saveFn) => {
        stopEditing();
        saveFn(editValue);
    };

    const handleEditKeyDown = (e, key, saveFn) => {
        if (e.key === "Enter") {
            stopEditing();
            saveFn(editValue);
        } else if (e.key === "Escape") {
            stopEditing();
        }
    };

    // Render editable cell
    const renderEditableCell = (cellKey, value, onSave, sx = {}) => {
        const isEditing = editingCell === cellKey;
        const remoteEditor = editingCells[cellKey];
        const remoteColor = remoteEditor ? socketIoUsers[remoteEditor.admin_uuid]?.color : null;

        if (isEditing) {
            return (
                <TextField
                    size="small"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => handleEditBlur(cellKey, onSave)}
                    onKeyDown={(e) => handleEditKeyDown(e, cellKey, onSave)}
                    inputRef={(el) => {
                        if (el && document.activeElement !== el) {
                            requestAnimationFrame(() => el.focus());
                        }
                    }}
                    variant="standard"
                    sx={{ minWidth: 80, ...sx }}
                />
            );
        }
        return (
            <Box sx={{ position: "relative" }}>
                {remoteEditor && (
                    <Tooltip title={`${remoteEditor.admin_name} is editing`} placement="top">
                        <Box
                            sx={{
                                position: "absolute",
                                top: -4,
                                right: -4,
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: remoteColor || "warning.main",
                                border: "1px solid white",
                                zIndex: 1,
                            }}
                        />
                    </Tooltip>
                )}
                <Typography
                    variant="body2"
                    onClick={() => startEditing(cellKey, value)}
                    sx={{
                        cursor: "pointer",
                        minHeight: 20,
                        minWidth: 60,
                        padding: "2px 4px",
                        borderRadius: 1,
                        "&:hover": { backgroundColor: "action.hover" },
                        ...(remoteEditor && {
                            outline: `2px solid ${remoteColor || "orange"}`,
                            borderRadius: 1,
                        }),
                        ...sx,
                    }}
                >
                    {value || "—"}
                </Typography>
            </Box>
        );
    };

    // Format progress entries
    const formatProgress = (progressEntries) => {
        if (!progressEntries || progressEntries.length === 0) return "—";
        return progressEntries.map((p) => `Module ${p.module || "?"} (${p.date}) - ${p.text}`).join("\n");
    };

    // Format date display
    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        const d = dayjs(dateStr).tz("America/New_York");
        const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
        return `${d.format("M/D")} (${dayNames[d.day()]})`;
    };

    // Count total rows for a course (for rowSpan calculation)
    const getTotalStudentRows = (course) => {
        return course.tests.reduce((sum, t) => sum + Math.max(t.students.length, 1), 0);
    };

    return (
        <Box m="20px">
            {/* Header with navigation */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h4" fontWeight="bold">
                        AP 테스트 진행상황
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        {Object.entries(activeUsers).map(([uuid, name]) => {
                            const userInfo = socketIoUsers[uuid];
                            return (
                                <Chip
                                    key={uuid}
                                    label={userInfo?.name || name}
                                    sx={(() => {
                                        const bg = userInfo?.color || "primary.main";
                                        let textColor = "white";
                                        if (bg.startsWith("#")) {
                                            const hex = bg.replace("#", "");
                                            const r = parseInt(hex.substring(0, 2), 16);
                                            const g = parseInt(hex.substring(2, 4), 16);
                                            const b = parseInt(hex.substring(4, 6), 16);
                                            textColor = (r * 299 + g * 587 + b * 114) / 1000 > 128 ? "black" : "white";
                                        }
                                        return { bgcolor: bg, color: textColor, border: `1px solid ${colors.primary[100]}` };
                                    })()}
                                />
                            );
                        })}
                    </Stack>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    {/* Hidden column pills */}
                    {hiddenCols.length > 0 && (
                        <Stack direction="row" spacing={0.5} sx={{ mr: 1 }}>
                            {hiddenCols.map((key) => {
                                const col = allColumns.find((c) => c.key === key);
                                return col ? (
                                    <Chip
                                        key={key}
                                        label={col.label}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => showColumn(key)}
                                        sx={{ fontSize: 11, height: 24, cursor: "pointer" }}
                                    />
                                ) : null;
                            })}
                        </Stack>
                    )}
                    <IconButton onClick={goToPreviousWeek} size="small">
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Chip
                        label={
                            semesterSeason && semesterWeekNum
                                ? `${seasonLabels[semesterSeason] || ""} Week ${semesterWeekNum}`
                                : "—"
                        }
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: "bold", fontSize: 14, px: 1 }}
                    />
                    <Typography variant="body2" color="textSecondary">
                        {thisWeek.length > 0
                            ? `${dayjs(thisWeek[0]).format("M/D")} – ${dayjs(thisWeek[6]).format("M/D")}`
                            : ""}
                    </Typography>
                    <IconButton onClick={goToNextWeek} size="small">
                        <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* Main table */}
            {loading ? (
                <Box display="flex" justifyContent="center" mt={10}>
                    <CircularProgress />
                </Box>
            ) : coursesData.length === 0 ? (
                <Box display="flex" justifyContent="center" mt={10}>
                    <Typography variant="h6" color="textSecondary">
                        이번 주에 Problem Solving 수업이 없습니다
                    </Typography>
                </Box>
            ) : (
                <Paper sx={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
                    {(() => {
                        const visCols = allColumns.filter(c => isVisible(c.key));
                        const gridTemplateColumns = visCols.map(c => `${getColWidth(c.key)}px`).join(" ");
                        const borderColor = theme.palette.divider;
                        const cellBase = { padding: "6px 8px", overflow: "hidden", textOverflow: "ellipsis", borderRight: `1px solid ${borderColor}`, borderBottom: `1px solid ${borderColor}`, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", minHeight: 36 };
                        const cellSx = (colKey, extra) => ({
                            ...cellBase,
                            ...extra,
                        });

                        // Helper: get column index in visible columns
                        const colIdx = (key) => { const i = visCols.findIndex(c => c.key === key); return i >= 0 ? i + 1 : null; };

                        return (
                            <Box sx={{ display: "inline-block", minWidth: "100%", borderRight: `1px solid ${theme.palette.divider}` }}>
                                {/* Header */}
                                <Box sx={{ display: "grid", gridTemplateColumns, backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "#f0f0f0", position: "sticky", top: 0, zIndex: 10 }}>
                                    {(() => {
                                        const headerCells = [];
                                        for (let i = 0; i < visCols.length; i++) {
                                            const col = visCols[i];
                                            const next = visCols[i + 1];
                                            // Merge reviewProgress + reviewProgressUnmatched into one wide header
                                            // when both are visible (the column "splits" visually). Same layout
                                            // as a normal header cell, just spanning 2 grid columns.
                                            if (col.key === "reviewProgress" && next?.key === "reviewProgressUnmatched") {
                                                headerCells.push(
                                                    <Box
                                                        key="reviewProgress-merged"
                                                        draggable={false}
                                                        sx={{ ...cellBase, gridColumn: "span 2", fontWeight: "bold", position: "relative", cursor: "context-menu", color: theme.palette.text.primary, userSelect: "none" }}
                                                        onContextMenu={(e) => { e.preventDefault(); hideColumn("reviewProgress"); }}
                                                        onDragStart={(e) => e.preventDefault()}
                                                    >
                                                        실제 리뷰 진도
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); toggleShowUnmatched(); }}
                                                            sx={{ ml: 0.5 }}
                                                            title="Hide unmatched"
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </IconButton>
                                                        <Box
                                                            onMouseDown={(e) => handleResizeStart(e, "reviewProgressUnmatched")}
                                                            sx={{ position: "absolute", right: -6, top: 0, bottom: 0, width: 12, cursor: "col-resize", zIndex: 1, "&:hover": { "&::after": { content: '""', position: "absolute", left: 5, top: 0, bottom: 0, width: 2, backgroundColor: "primary.main" } } }}
                                                        />
                                                    </Box>
                                                );
                                                i++; // skip the next column since we merged it
                                                continue;
                                            }
                                            // Standard header cell — but if this is the matched column rendered alone,
                                            // add the show-unmatched toggle button (toggle is currently off).
                                            const isReviewProgressAlone = col.key === "reviewProgress" && !showUnmatched;
                                            headerCells.push(
                                                <Box
                                                    key={col.key}
                                                    draggable={false}
                                                    sx={{ ...cellBase, fontWeight: "bold", position: "relative", cursor: "context-menu", color: theme.palette.text.primary, userSelect: "none" }}
                                                    onContextMenu={(e) => { e.preventDefault(); hideColumn(col.key); }}
                                                    onDragStart={(e) => e.preventDefault()}
                                                >
                                                    {col.label}
                                                    {isReviewProgressAlone && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => { e.stopPropagation(); toggleShowUnmatched(); }}
                                                            sx={{ ml: 0.5 }}
                                                            title="Show unmatched"
                                                        >
                                                            <VisibilityOffIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                    <Box
                                                        onMouseDown={(e) => handleResizeStart(e, col.key)}
                                                        sx={{ position: "absolute", right: -6, top: 0, bottom: 0, width: 12, cursor: "col-resize", zIndex: 1, "&:hover": { "&::after": { content: '""', position: "absolute", left: 5, top: 0, bottom: 0, width: 2, backgroundColor: "primary.main" } } }}
                                                    />
                                                </Box>
                                            );
                                        }
                                        return headerCells;
                                    })()}
                                </Box>

                                {/* Body — each course is one grid so merged cells can span rows */}
                                {coursesData.map((course, courseIdx) => {
                                    const totalRows = course.tests.reduce((sum, t) => sum + Math.max(t.students.length, 1), 0);

                                    // 실제 시험 결과 is a per-(student, course) value — collect each
                                    // student once (across all of the course's tests) for the
                                    // course-spanning 실제 시험 결과 column.
                                    const courseStudents = [];
                                    const seenActualResultStudents = new Set();
                                    course.tests.forEach((t) => {
                                        (t.students || []).forEach((s) => {
                                            if (s && !seenActualResultStudents.has(s.student_id)) {
                                                seenActualResultStudents.add(s.student_id);
                                                courseStudents.push(s);
                                            }
                                        });
                                    });

                                    // Build flat list of rows for this course
                                    const courseRows = [];
                                    course.tests.forEach((test, testIdx) => {
                                        const isLastTest = testIdx === course.tests.length - 1;
                                        if (test.students.length === 0) {
                                            courseRows.push({ test, student: null, studentCount: 1, isLastTest });
                                        } else {
                                            test.students.forEach((student, si) => {
                                                courseRows.push({ test, student, studentIdx: si, studentCount: test.students.length, isLastTest, isLastStudent: si === test.students.length - 1 });
                                            });
                                        }
                                    });

                                    return (
                                        <React.Fragment key={course.course_id}>
                                            <Box sx={{
                                                display: "grid",
                                                gridTemplateColumns,
                                                gridTemplateRows: `repeat(${totalRows}, auto)`,
                                                borderTop: courseIdx > 0 ? `2px solid ${borderColor}` : undefined,
                                            }}>
                                                {/* Course-level merged cells */}
                                                {isVisible("course") && (
                                                    <Box sx={cellSx("course", { gridRow: `1 / span ${totalRows}`, gridColumn: colIdx("course"), fontWeight: "bold", backgroundColor: "action.selected" })}>
                                                        <a href={`/classrooms/${course.course_uuid}/home`} target="_blank" rel="noopener noreferrer"
                                                            style={{ color: "inherit", textDecoration: "none" }}
                                                            onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                                                            onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                                                        >{course.course_title}</a>
                                                    </Box>
                                                )}
                                                {isVisible("classroom") && (
                                                    <Box sx={cellSx("classroom", { gridRow: `1 / span ${totalRows}`, gridColumn: colIdx("classroom"), backgroundColor: "action.selected" })}>
                                                        {course.classroom || "—"}
                                                    </Box>
                                                )}
                                                {isVisible("teacher") && (
                                                    <Box sx={cellSx("teacher", { gridRow: `1 / span ${totalRows}`, gridColumn: colIdx("teacher"), backgroundColor: "action.selected" })}>
                                                        {course.teacher || "—"}
                                                    </Box>
                                                )}
                                                {/* reviewProgress is rendered per-test in the test loop below.
                                                    The unmatched sub-column (when toggle is on) is course-level. */}
                                                {isVisible("reviewProgressUnmatched") && (
                                                    <Box sx={cellSx("reviewProgressUnmatched", { gridRow: `1 / span ${totalRows}`, gridColumn: colIdx("reviewProgressUnmatched"), backgroundColor: "action.selected", flexDirection: "column", alignItems: "stretch", justifyContent: "center" })}>
                                                        {(course.unmatched_progress || []).length > 0 ? course.unmatched_progress.map((p, i) => (
                                                            <Box
                                                                key={`${p.event_id}-${p.slot}`}
                                                                onClick={(e) => handleEntryClick(e, p, course)}
                                                                sx={{ py: 0.5, px: 0.5, borderBottom: i < course.unmatched_progress.length - 1 ? '1px solid' : 'none', borderColor: 'divider', cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                                            >
                                                                <Typography variant="body2" sx={{ fontSize: 12, textAlign: "left" }}>
                                                                    Module {p.module || "?"} ({p.date}) - {p.text}
                                                                </Typography>
                                                            </Box>
                                                        )) : <Typography variant="body2" color="text.secondary">—</Typography>}
                                                    </Box>
                                                )}

                                                {/* 실제 시험 결과 is per-(student, course): one tall cell that
                                                    spans the whole course and lists each student once. */}
                                                {isVisible("actualResult") && (
                                                    <Box sx={cellSx("actualResult", { gridRow: `1 / span ${totalRows}`, gridColumn: colIdx("actualResult"), backgroundColor: "action.selected", flexDirection: "column", alignItems: "stretch", justifyContent: "center" })}>
                                                        {courseStudents.length > 0 ? courseStudents.map((s, i) => (
                                                            <Box
                                                                key={s.student_id}
                                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.5, px: 0.5, borderBottom: i < courseStudents.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
                                                            >
                                                                <Typography variant="body2" title={`${s.student_name}${s.korean_name ? ` ${s.korean_name}` : ""}${s.consultation_number ? ` (${s.consultation_number})` : ""}`} sx={{ fontSize: 12, textAlign: "left", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                    {s.student_name}{s.korean_name ? ` ${s.korean_name}` : ""}{s.consultation_number ? ` (${s.consultation_number})` : ""}
                                                                </Typography>
                                                                {renderEditableCell(`result-${course.course_id}-${s.student_id}`, s.actual_exam_result, (val) => saveActualResult(s.student_id, course.course_id, val ? parseInt(val) : null), { minWidth: 40 })}
                                                            </Box>
                                                        )) : <Typography variant="body2" color="text.secondary">—</Typography>}
                                                    </Box>
                                                )}

                                                {/* Test + student rows */}
                                                {(() => {
                                                    let rowNum = 0;
                                                    return course.tests.map((test, testIdx) => {
                                                        const students = test.students.length > 0 ? test.students : [null];
                                                        const studentCount = students.length;
                                                        const testStartRow = rowNum + 1;
                                                        const isLastTest = testIdx === course.tests.length - 1;
                                                        const testBorderBot = !isLastTest ? "2px solid #bdbdbd" : `1px solid ${borderColor}`;

                                                        const testCells = [];

                                                        // Test-level merged cells
                                                        if (isVisible("testTitle")) {
                                                            testCells.push(
                                                                <Box key={`tt-${test.course_test_id}`} sx={cellSx("testTitle", { gridRow: `${testStartRow} / span ${studentCount}`, gridColumn: colIdx("testTitle"), fontWeight: 500, borderBottom: testBorderBot })}>
                                                                    {test.course_test_id ? <a href={`/classrooms/${course.course_uuid}/test-v2/${test.course_test_id}/grading`} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none" }} onMouseEnter={(e) => e.target.style.textDecoration = "underline"} onMouseLeave={(e) => e.target.style.textDecoration = "none"}>{test.test_title || "—"}</a> : (test.test_title || "—")}
                                                                </Box>
                                                            );
                                                        }
                                                        if (isVisible("source")) {
                                                            testCells.push(
                                                                <Box key={`src-${test.course_test_id}`} sx={cellSx("source", { gridRow: `${testStartRow} / span ${studentCount}`, gridColumn: colIdx("source"), borderBottom: testBorderBot })}>
                                                                    {renderEditableCell(`source-${test.course_test_id}`, test.source_text, (val) => saveManualField(test.course_test_id, "source_text", val))}
                                                                </Box>
                                                            );
                                                        }
                                                        if (isVisible("reviewSchedule")) {
                                                            testCells.push(
                                                                <Box key={`rev-${test.course_test_id}`} sx={cellSx("reviewSchedule", { gridRow: `${testStartRow} / span ${studentCount}`, gridColumn: colIdx("reviewSchedule"), borderBottom: testBorderBot })}>
                                                                    {renderEditableCell(`review-${test.course_test_id}`, test.review_schedule, (val) => saveManualField(test.course_test_id, "review_schedule", val))}
                                                                </Box>
                                                            );
                                                        }
                                                        if (isVisible("reviewProgress")) {
                                                            testCells.push(
                                                                <Box key={`rp-${test.course_test_id}`} sx={cellSx("reviewProgress", { gridRow: `${testStartRow} / span ${studentCount}`, gridColumn: colIdx("reviewProgress"), borderBottom: testBorderBot, flexDirection: "column", alignItems: "stretch", justifyContent: "center" })}>
                                                                    {(test.review_progress || []).length > 0 ? test.review_progress.map((p, i) => {
                                                                        const isRegexMatched = p.test_ref == null;
                                                                        return (
                                                                            <Box
                                                                                key={`${p.event_id}-${p.slot}`}
                                                                                onClick={(e) => handleEntryClick(e, p, course)}
                                                                                sx={{ py: 0.5, px: 0.5, borderBottom: i < test.review_progress.length - 1 ? '1px solid' : 'none', borderColor: 'divider', cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' }, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}
                                                                            >
                                                                                <Typography variant="body2" sx={{ fontSize: 12, textAlign: "left", flex: 1 }}>
                                                                                    Module {p.module || "?"} ({p.date}) - {p.text}
                                                                                </Typography>
                                                                                {isRegexMatched && (
                                                                                    <Tooltip title="Matched by regex (no explicit assignment)">
                                                                                        <PriorityHighIcon sx={{ fontSize: 14, color: 'warning.main', flexShrink: 0, mt: 0.25 }} />
                                                                                    </Tooltip>
                                                                                )}
                                                                            </Box>
                                                                        );
                                                                    }) : <Typography variant="body2" color="text.secondary">—</Typography>}
                                                                </Box>
                                                            );
                                                        }
                                                        if (isVisible("paperOutDate")) {
                                                            testCells.push(
                                                                <Box key={`paper-${test.course_test_id}`} sx={cellSx("paperOutDate", { gridRow: `${testStartRow} / span ${studentCount}`, gridColumn: colIdx("paperOutDate"), borderBottom: testBorderBot })}>
                                                                    {renderEditableCell(`paper-${test.course_test_id}`, test.paper_out_date, (val) => saveManualField(test.course_test_id, "paper_out_date", val))}
                                                                </Box>
                                                            );
                                                        }

                                                        // Student-level cells
                                                        const studentCells = students.map((student, si) => {
                                                            rowNum++;
                                                            const r = rowNum;
                                                            const isLast = si === students.length - 1;
                                                            const studentBorderBot = isLast ? testBorderBot : `1px solid ${borderColor}`;

                                                            if (!student) {
                                                                // Empty test
                                                                return (
                                                                    <React.Fragment key={`empty-${test.course_test_id}`}>
                                                                        {isVisible("student") && <Box sx={cellSx("student", { gridRow: r, gridColumn: colIdx("student"), color: "text.secondary", fontStyle: "italic", borderBottom: studentBorderBot })}>학생 없음</Box>}
                                                                        {isVisible("assignedDate") && <Box sx={cellSx("assignedDate", { gridRow: r, gridColumn: colIdx("assignedDate"), borderBottom: studentBorderBot })}>—</Box>}
                                                                        {isVisible("dueDate") && <Box sx={cellSx("dueDate", { gridRow: r, gridColumn: colIdx("dueDate"), borderBottom: studentBorderBot })}>—</Box>}
                                                                        {isVisible("submitted") && <Box sx={cellSx("submitted", { gridRow: r, gridColumn: colIdx("submitted"), borderBottom: studentBorderBot })}>—</Box>}
                                                                        {isVisible("submittedAt") && <Box sx={cellSx("submittedAt", { gridRow: r, gridColumn: colIdx("submittedAt"), borderBottom: studentBorderBot })}>—</Box>}
                                                                        {isVisible("adminSubmittedAt") && <Box sx={cellSx("adminSubmittedAt", { gridRow: r, gridColumn: colIdx("adminSubmittedAt"), borderBottom: studentBorderBot })}>—</Box>}
                                                                        {isVisible("mcq") && <Box sx={cellSx("mcq", { gridRow: r, gridColumn: colIdx("mcq"), borderBottom: studentBorderBot })}>—</Box>}
                                                                        {isVisible("frq") && <Box sx={cellSx("frq", { gridRow: r, gridColumn: colIdx("frq"), borderBottom: studentBorderBot })}>—</Box>}
                                                                        {isVisible("apScore") && <Box sx={cellSx("apScore", { gridRow: r, gridColumn: colIdx("apScore"), borderBottom: studentBorderBot })}>—</Box>}
                                                                    </React.Fragment>
                                                                );
                                                            }

                                                            return (
                                                                <React.Fragment key={`${test.course_test_id}-${student.student_id}`}>
                                                                    {isVisible("student") && <Box sx={cellSx("student", { gridRow: r, gridColumn: colIdx("student"), borderBottom: studentBorderBot })}>{student.student_name}{student.korean_name ? ` ${student.korean_name}` : ""}{student.consultation_number ? ` (${student.consultation_number})` : ""}</Box>}
                                                                    {isVisible("assignedDate") && <Box sx={cellSx("assignedDate", { gridRow: r, gridColumn: colIdx("assignedDate"), borderBottom: studentBorderBot })}><Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>{formatDate(student.assigned_date)}{test.homework_email_sent && <Tooltip title="Homework email sent"><CheckCircleIcon sx={{ color: "success.main", fontSize: 14 }} /></Tooltip>}</Box></Box>}
                                                                    {isVisible("dueDate") && <Box sx={cellSx("dueDate", { gridRow: r, gridColumn: colIdx("dueDate"), color: student.is_completed ? "success.main" : (student.due_date && dayjs(student.due_date).isBefore(dayjs()) ? "error.main" : undefined), borderBottom: studentBorderBot, gap: 0.5 })}>
    {formatDate(student.due_date)}
    {(() => {
        const rc = test.reminder_counts?.[student.student_id] || 0;
        return rc > 0 ? (
            <Box onClick={(e) => { e.stopPropagation(); handleReminderClick(test.course_test_id, student.student_id); }}
                sx={{ width: 18, height: 18, borderRadius: "50%", border: "1.5px solid", borderColor: rc >= 3 ? "error.main" : "warning.main", color: rc >= 3 ? "error.main" : "warning.main", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, "&:hover": { opacity: 0.7 } }}>
                {rc}
            </Box>
        ) : (
            <Box onClick={(e) => { e.stopPropagation(); handleReminderClick(test.course_test_id, student.student_id); }}
                sx={{ width: 18, height: 18, borderRadius: "50%", border: "1.5px dashed", borderColor: "text.disabled", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, opacity: 0.4, "&:hover": { opacity: 0.8, borderColor: "warning.main" } }}>
                0
            </Box>
        );
    })()}
</Box>}
                                                                    {isVisible("submitted") && <Box sx={cellSx("submitted", { gridRow: r, gridColumn: colIdx("submitted"), borderBottom: studentBorderBot })}>{student.is_completed ? <CheckCircleIcon sx={{ color: "success.main", fontSize: 18 }} /> : <RadioButtonUncheckedIcon sx={{ color: "text.disabled", fontSize: 18 }} />}</Box>}
                                                                    {isVisible("submittedAt") && <Box sx={cellSx("submittedAt", { gridRow: r, gridColumn: colIdx("submittedAt"), borderBottom: studentBorderBot, fontSize: 11 })}>{student.submitted_at ? dayjs(student.submitted_at).tz("America/New_York").format("M/D h:mma") : "—"}</Box>}
                                                                    {isVisible("adminSubmittedAt") && <Box sx={cellSx("adminSubmittedAt", { gridRow: r, gridColumn: colIdx("adminSubmittedAt"), borderBottom: studentBorderBot, fontSize: 11 })}>{student.admin_submitted_at ? dayjs(student.admin_submitted_at).tz("America/New_York").format("M/D h:mma") : "—"}</Box>}
                                                                    {isVisible("mcq") && <Box sx={cellSx("mcq", { gridRow: r, gridColumn: colIdx("mcq"), borderBottom: studentBorderBot })}>{student.mcq_score != null ? `${student.mcq_score}/${student.mcq_total}` : "—"}</Box>}
                                                                    {isVisible("frq") && <Box sx={cellSx("frq", { gridRow: r, gridColumn: colIdx("frq"), borderBottom: studentBorderBot })}>{student.frq_grading_status === "grading" ? <Chip label="Grading..." size="small" color="warning" variant="outlined" sx={{ fontSize: 11, height: 22 }} /> : student.frq_grading_status === "failed" ? <Chip label="Failed" size="small" color="error" variant="outlined" sx={{ fontSize: 11, height: 22 }} /> : student.frq_score != null ? `${student.frq_score}/${student.frq_total}` : "—"}</Box>}
                                                                    {isVisible("apScore") && <Box sx={cellSx("apScore", { gridRow: r, gridColumn: colIdx("apScore"), borderBottom: studentBorderBot })}>{student.ap_score != null ? student.ap_score : "—"}</Box>}
                                                                </React.Fragment>
                                                            );
                                                        });

                                                        return [...testCells, ...studentCells];
                                                    });
                                                })()}
                                            </Box>

                                            {/* Spacer between courses */}
                                            {courseIdx < coursesData.length - 1 && (
                                                <Box sx={{ height: 12, backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" }} />
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </Box>
                        );
                    })()}
                </Paper>
            )}

            {/* Inline test reassignment popover */}
            <Menu
                anchorEl={reassignMenu?.anchorEl ?? null}
                open={Boolean(reassignMenu?.anchorEl)}
                onClose={() => setReassignMenu(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                MenuListProps={{ dense: true }}
            >
                <MenuItem
                    onClick={() => handleReassignSelect(null)}
                    selected={reassignMenu?.currentTestRef == null}
                >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                        {reassignMenu?.currentTestRef == null && <CheckIcon fontSize="small" />}
                    </ListItemIcon>
                    N/A
                </MenuItem>
                {(reassignMenu?.courseTests || [])
                    .filter((t) => t.test_id != null)
                    .map((t) => (
                        <MenuItem
                            key={t.test_id}
                            onClick={() => handleReassignSelect(t.test_id)}
                            selected={reassignMenu?.currentTestRef === t.test_id}
                        >
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                {reassignMenu?.currentTestRef === t.test_id && <CheckIcon fontSize="small" />}
                            </ListItemIcon>
                            {t.test_title}
                        </MenuItem>
                    ))}
            </Menu>
        </Box>
    );
};

export default APTestProgress;
